/**
 * Timeline Playback Hook
 *
 * Manages client-side playback sequencing.
 * Handles play/pause, scrubbing, and frame-accurate playhead movement.
 * Uses requestAnimationFrame for smooth playhead animation.
 *
 * Dual-channel playhead: positionRef updates every frame for smooth DOM updates,
 * while React state (setPlayhead) is throttled to ~10Hz to avoid render storms.
 */

import { useRef, useCallback, useEffect } from 'react'
import type { TimelineState } from './types'

interface UseTimelinePlaybackProps {
  /** Current timeline state */
  timelineState: TimelineState

  /** Set playhead position */
  setPlayhead: (position: number) => void

  /** Toggle play/pause */
  togglePlayPause: () => void
}

interface UseTimelinePlaybackReturn {
  /** Start playback */
  play: () => void

  /** Pause playback */
  pause: () => void

  /** Toggle play/pause */
  togglePlayPause: () => void

  /** Move playhead by delta (seconds) */
  scrub: (delta: number) => void

  /** Jump to start */
  jumpToStart: () => void

  /** Jump to end */
  jumpToEnd: () => void

  /** Ref that tracks playhead position at 60fps (for direct DOM updates) */
  playheadPositionRef: React.MutableRefObject<number>
}

/** Keep a ref always in sync with the latest value */
function useLatest<T>(value: T) {
  const ref = useRef(value)
  ref.current = value
  return ref
}

/** Throttle interval for React state updates during playback (ms) */
const STATE_UPDATE_INTERVAL_MS = 100

export function useTimelinePlayback({
  timelineState,
  setPlayhead,
  togglePlayPause,
}: UseTimelinePlaybackProps): UseTimelinePlaybackReturn {
  const animationFrameRef = useRef<number | null>(null)
  const lastUpdateTimeRef = useRef<number>(Date.now())
  const lastStateUpdateTimeRef = useRef<number>(0)
  const positionRef = useRef(timelineState.playheadPosition)

  // Keep stable refs to avoid stale closures in the animation loop
  const totalDurationRef = useLatest(timelineState.totalDuration)
  const setPlayheadRef = useLatest(setPlayhead)
  const togglePlayPauseRef = useLatest(togglePlayPause)
  const latestPlayheadRef = useLatest(timelineState.playheadPosition)

  // Sync positionRef from state when NOT playing (so scrubbing works)
  useEffect(() => {
    if (!timelineState.isPlaying) {
      positionRef.current = timelineState.playheadPosition
      return
    }

    // External seeks while playing must reset the RAF baseline. Without this,
    // the next animation frame advances from the pre-seek ref position.
    if (Math.abs(positionRef.current - timelineState.playheadPosition) > 0.25) {
      positionRef.current = timelineState.playheadPosition
      lastUpdateTimeRef.current = Date.now()
      lastStateUpdateTimeRef.current = Date.now()
    }
  }, [timelineState.playheadPosition, timelineState.isPlaying])

  // Start playback
  const play = useCallback(() => {
    togglePlayPause()
  }, [togglePlayPause])

  // Pause playback
  const pause = useCallback(() => {
    togglePlayPause()
  }, [togglePlayPause])

  // Move playhead by delta
  const scrub = useCallback(
    (delta: number) => {
      const newPosition = Math.max(
        0,
        Math.min(timelineState.playheadPosition + delta, timelineState.totalDuration)
      )
      setPlayhead(newPosition)
    },
    [timelineState.playheadPosition, timelineState.totalDuration, setPlayhead]
  )

  // Jump to start
  const jumpToStart = useCallback(() => {
    setPlayhead(0)
  }, [setPlayhead])

  // Jump to end
  const jumpToEnd = useCallback(() => {
    setPlayhead(timelineState.totalDuration)
  }, [timelineState.totalDuration, setPlayhead])

  // Playback loop using requestAnimationFrame
  useEffect(() => {
    if (!timelineState.isPlaying) {
      // Cancel animation frame if paused
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      return
    }

    // Seed positionRef from current state when playback starts
    positionRef.current = latestPlayheadRef.current
    lastUpdateTimeRef.current = Date.now()
    lastStateUpdateTimeRef.current = Date.now()

    const animate = () => {
      const now = Date.now()
      const deltaMs = now - lastUpdateTimeRef.current
      lastUpdateTimeRef.current = now

      // Convert milliseconds to seconds
      const deltaSeconds = deltaMs / 1000

      // Move playhead forward using the ref (avoids stale closure)
      const newPosition = positionRef.current + deltaSeconds

      if (newPosition >= totalDurationRef.current) {
        // Reached end of timeline - stop playback
        positionRef.current = totalDurationRef.current
        setPlayheadRef.current(totalDurationRef.current)
        togglePlayPauseRef.current() // Stop playback
      } else {
        // Update ref every frame (for smooth DOM updates via TimelinePlayhead)
        positionRef.current = newPosition

        // Throttle React state updates to ~10Hz to avoid render storms
        if (now - lastStateUpdateTimeRef.current >= STATE_UPDATE_INTERVAL_MS) {
          lastStateUpdateTimeRef.current = now
          setPlayheadRef.current(newPosition)
        }

        animationFrameRef.current = requestAnimationFrame(animate)
      }
    }

    // Start animation loop
    animationFrameRef.current = requestAnimationFrame(animate)

    // Cleanup
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [timelineState.isPlaying, totalDurationRef, setPlayheadRef, togglePlayPauseRef, latestPlayheadRef]) // Only re-run when play state changes

  return {
    play,
    pause,
    togglePlayPause,
    scrub,
    jumpToStart,
    jumpToEnd,
    playheadPositionRef: positionRef,
  }
}
